import { useState } from 'react';
import { RefreshCw, RotateCcw, XCircle, Trash2 } from 'lucide-react';
import type { RecordModel } from 'pocketbase';
import { useCollection } from '../../lib/hooks';
import { pb, formatWhen, errorText } from '../../lib/pb';
import { confirmDialog } from '../ConfirmDialog';

interface JobRecord extends RecordModel {
  kind: string;
  campaign: string;
  run: string;
  owner: string;
  status: string;
  attempts: number;
  max_attempts: number;
  run_at: string;
  claimed_by: string;
  lease_until: string;
  last_error: string;
}

const STATUS_STYLE: Record<string, string> = {
  queued: 'bg-stone-100 text-natural-text/60',
  claimed: 'bg-natural-sage/15 text-natural-sage',
  done: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-600',
  cancelled: 'bg-stone-100 text-natural-text/50',
};

/**
 * The job queue.
 *
 * Useful when something looks stuck. Note that a worker which dies mid-job has
 * its lease expire and the job returned to the queue automatically within a
 * minute — most "stuck" jobs unstick themselves, so check the timestamp before
 * retrying by hand.
 */
export default function QueueInspector() {
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const jobs = useCollection<JobRecord>('jobs', {
    sort: '-created',
    ...(statusFilter ? { filter: `status = "${statusFilter}"` } : {}),
  });

  const act = async (id: string, patch: Record<string, unknown>) => {
    setBusy(id);
    setError(null);
    try {
      await pb.collection('jobs').update(id, patch);
      await jobs.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id: string) => {
    if (
      !(await confirmDialog({
        title: 'Delete this job record?',
        message: 'This does not undo work already done — it only removes the queue entry.',
        confirmLabel: 'Delete',
        danger: true,
      }))
    ) {
      return;
    }
    setBusy(id);
    try {
      await pb.collection('jobs').delete(id);
      await jobs.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="zen-card p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg font-medium">Queue</h3>
          <p className="text-[11px] text-natural-text/55 mt-0.5">
            An abandoned job is reclaimed automatically once its lease expires — usually within a
            minute of a worker dying.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="zen-input py-1.5 text-xs w-32"
          >
            <option value="">All</option>
            <option value="queued">Queued</option>
            <option value="claimed">Running</option>
            <option value="failed">Failed</option>
            <option value="done">Done</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={() => void jobs.reload()} className="zen-btn-ghost py-1.5 px-3 text-xs">
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full text-xs min-w-[820px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-natural-text/45 border-b border-natural-border">
              <th className="pb-2 font-bold">Kind</th>
              <th className="pb-2 font-bold w-24">Status</th>
              <th className="pb-2 font-bold w-20">Tries</th>
              <th className="pb-2 font-bold w-28">Due</th>
              <th className="pb-2 font-bold w-32">Worker</th>
              <th className="pb-2 font-bold">Last error</th>
              <th className="pb-2 font-bold w-28 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.items.map((job) => (
              <tr key={job.id} className="border-b border-natural-border/50 align-top">
                <td className="py-3 font-mono text-[11px]">{job.kind}</td>
                <td className="py-3">
                  <span className={`zen-pill ${STATUS_STYLE[job.status] ?? ''}`}>{job.status}</span>
                </td>
                <td className="py-3 font-mono text-[11px] text-natural-text/60">
                  {job.attempts}/{job.max_attempts}
                </td>
                <td className="py-3 text-[10px] text-natural-text/50">{formatWhen(job.run_at)}</td>
                <td className="py-3 font-mono text-[10px] text-natural-text/50 truncate max-w-[8rem]">
                  {job.claimed_by || '—'}
                </td>
                <td className="py-3 text-[10px] text-red-600/80 break-words max-w-xs">
                  {job.last_error || '—'}
                </td>
                <td className="py-3 text-right whitespace-nowrap">
                  {(job.status === 'failed' || job.status === 'cancelled') && (
                    <button
                      onClick={() =>
                        void act(job.id, {
                          status: 'queued',
                          attempts: 0,
                          claimed_by: '',
                          lease_until: '',
                          run_at: new Date().toISOString().replace('T', ' ').replace('Z', 'Z'),
                        })
                      }
                      disabled={busy === job.id}
                      className="zen-btn-ghost py-1 px-2 text-[10px] mr-1"
                      title="Requeue"
                    >
                      <RotateCcw size={11} />
                    </button>
                  )}

                  {(job.status === 'queued' || job.status === 'claimed') && (
                    <button
                      onClick={() =>
                        void act(job.id, {
                          status: 'cancelled',
                          claimed_by: '',
                          lease_until: '',
                          last_error: 'Cancelled from the queue inspector',
                        })
                      }
                      disabled={busy === job.id}
                      className="zen-btn-danger py-1 px-2 text-[10px] mr-1"
                      title="Cancel"
                    >
                      <XCircle size={11} />
                    </button>
                  )}

                  <button
                    onClick={() => void remove(job.id)}
                    disabled={busy === job.id}
                    className="zen-btn-ghost py-1 px-2 text-[10px]"
                    title="Delete record"
                  >
                    <Trash2 size={11} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!jobs.loading && jobs.items.length === 0 && (
          <p className="text-xs text-natural-text/40 py-10 text-center">The queue is empty.</p>
        )}
      </div>
    </div>
  );
}
